/** @jsx CanvaSX */
import { CanvaSX } from '@lib/canvasx/canvasx';
import { FlexRow } from '@lib/canvasx/components/FlexRow';
import {
  type FieldContentsData,
  type WhiteboardRecordModels,
} from '../shared-types';
import { IconField } from './field_contents/IconField';
import { PillField } from './field_contents/PillField';
import { ProgressField } from './field_contents/ProgressField';
import { TagField } from './field_contents/TagField';
import { TextField } from './field_contents/TextField';
import { AvatarField } from './field_contents/AvatarField';
import { raw } from '@aha-app/mvc';
import { canvasCssValue } from '@lib/canvasx/util';
import { IntegrationField } from './field_contents/IntegrationField';

// Be waring of changing the types here, as we serialize CardContent into the
// document. Any changes here may have implications for backwards compatibility.
export type CardFieldContent = FieldContentsData & { fieldId: RecordId };
export type CardGroupContent = CardFieldContent[];
export type CardRowContent =
  | { type: 'divider' }
  | { type: 'content'; content: [CardGroupContent, CardGroupContent] };
export type CardContent = CardRowContent[];
export interface CardStyling {
  backgroundColor?: string;
  highlightColor?: string;
}
export interface CardData {
  styling: CardStyling;
  content: CardContent;
}

type FieldComponentTypes = {
  [k in keyof FieldContentsData]: CanvaSXFC<
    NonNullable<Pick<FieldContentsData, k>> & {
      contents: FieldContentsData;
      fieldId: RecordId;
    }
  >;
};

// The order of these components is significant, as it renders each type of content in order
// using Object.keys().
const FieldComponents: FieldComponentTypes = {
  icon: IconField,
  avatar: AvatarField,
  pill: PillField,
  progress: ProgressField,
  tags: TagField,
  text: TextField,
  integrations: IntegrationField,

  // Unsupported keys:
  // externalUrl
};

export function fieldContent(
  record: WhiteboardRecordModels,
  cardField: Aha.CardField
): CardFieldContent | null {
  const { field } = cardField;

  // No content for this field, or the field does not exist anymore
  if (!field) {
    return null;
  }

  return { fieldId: cardField.id, ...field.contentsForRecord(record) };
}

export function renderFieldContents(contents: CardFieldContent) {
  const { fieldId } = contents;
  const fieldContents: CanvaSXNode[] = [];

  // Build content
  Object.keys(FieldComponents).forEach(
    (contentKey: keyof FieldContentsData) => {
      const content = contents[contentKey];
      if (content !== null && content !== undefined) {
        const Component = FieldComponents[contentKey]!;
        const props = { [contentKey]: content };

        // Tags are an array, so process each one separately
        switch (contentKey) {
          case 'tags':
            if (Array.isArray(content)) {
              content.forEach(tag => {
                fieldContents.push(
                  <TagField fieldId={fieldId} tag={tag} contents={contents} />
                );
              });
            }
            break;

          case 'integrations':
            if (Array.isArray(content)) {
              content.forEach(integration => {
                fieldContents.push(
                  <IntegrationField
                    fieldId={fieldId}
                    integration={integration}
                    contents={contents}
                  />
                );
              });
            }
            break;

          default:
            fieldContents.push(
              <Component {...props} fieldId={fieldId} contents={contents} />
            );
            break;
        }
      }
    }
  );

  if (fieldContents.length === 0) {
    return null;
  }

  // If there's only one child, then return it as the only node, so that the main card <FlexRow>
  // can wrap the text accordingly.
  if (fieldContents.length === 1) {
    return fieldContents[0];
  }

  return (
    <FlexRow gap={{ horizontal: 3, vertical: 0 }} verticalAlignment='center'>
      {fieldContents}
    </FlexRow>
  );
}

function prepareGroupContent(
  record: WhiteboardRecordModels,
  group: Aha.CardGroup
) {
  return group.fields.map(field => fieldContent(record, field)).filter(Boolean);
}

function generateContentForRecord(
  record: WhiteboardRecordModels,
  cardView: Aha.CardView
): CardContent {
  // Sometimes the card view might be proxied, so un-proxy it
  return raw(cardView.rows).map(row => {
    if (row.divider) {
      return { type: 'divider' };
    }
    const firstGroupContent = prepareGroupContent(record, row.firstGroup);
    const secondGroupContent = prepareGroupContent(record, row.secondGroup);
    return {
      type: 'content',
      content: [firstGroupContent, secondGroupContent],
    };
  });
}

function generateCardStyling(
  record: WhiteboardRecordModels,
  cardView: Aha.CardView
): CardStyling {
  let backgroundColor: string | undefined;
  let highlightColor: string | undefined;

  const { cardStyling } = cardView;
  if (cardStyling?.styling) {
    const stylingData = cardStyling.styling.cardStyling(record);

    if (stylingData.highlightColor) {
      highlightColor = stylingData.highlightColor;
      if (stylingData.useBackgroundColor) {
        backgroundColor = stylingData.highlightColor;
      }
    }
  } else {
    switch (record.typename) {
      case 'Task':
      case 'Page':
      case 'PdlcStep':
      case 'IdeaTheme':
      case 'ReleasePhase':
      case 'Project':
        highlightColor = canvasCssValue('--theme-primary-border', '#e1e1e1');
        break;

      default:
        highlightColor = record.workflowStatus
          ? `${record.workflowStatus?.colorObject.hex()}ee`
          : canvasCssValue('--theme-primary-border', '#e1e1e1');
        break;
    }
  }

  return { backgroundColor, highlightColor };
}

export function generateCardDataForRecord(
  record: WhiteboardRecordModels,
  cardView: Aha.CardView
): CardData {
  return {
    styling: generateCardStyling(record, cardView),
    content: generateContentForRecord(record, cardView),
  };
}
